import { useState, useMemo } from 'react'
import { CheckCircle, Plus, HelpCircle } from '@/ui/icons'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/ui/components/primitives/tooltip'
import { GEAR_CHECKLIST_TOOLTIP_COPY } from '@/shared/copy/tooltips'

const INITIAL_GEAR_VISIBLE = 4
import { useTripDetailPanel } from '../contexts/TripDetailPanelContext'
import { useGearNeeds } from '@/modules/gear/hooks/useGearNeeds'
import { getGearQuantities } from '@/shared/api/gearNeeds'
import { SectionCard } from './SectionCard'
import { Button } from '@/ui/design-system'
import { cn } from '@/shared/utils'

interface GearSummarySectionProps {
  tripId: string
  isGuestAccess?: boolean
  title?: string
  className?: string
}

export function GearSummarySection({
  tripId,
  isGuestAccess = false,
  title = 'Gear Checklist',
  className
}: GearSummarySectionProps) {
  const { setSelectedDetail, selectedDetail } = useTripDetailPanel()
  const { gearNeeds, isLoading } = useGearNeeds(tripId)

  const { totalItems, totalNeeded, claimedCount, progressPercent } = useMemo(() => {
    const total = gearNeeds?.length ?? 0
    if (total === 0) return { totalItems: 0, totalNeeded: 0, claimedCount: 0, progressPercent: 0 }
    let claimed = 0
    gearNeeds?.forEach((need) => {
      const { totalConfirmedQuantity } = getGearQuantities(need)
      claimed += Math.min(totalConfirmedQuantity, need.quantity_needed)
    })
    const needed = gearNeeds?.reduce((sum, n) => sum + n.quantity_needed, 0) ?? 0
    const pct = needed > 0 ? Math.round((claimed / needed) * 100) : 0
    return { totalItems: total, totalNeeded: needed, claimedCount: claimed, progressPercent: pct }
  }, [gearNeeds])

  const currentGearId = selectedDetail?.type === 'gear' ? selectedDetail.id : null
  const [expanded, setExpanded] = useState(false)
  const displayNeeds = expanded ? (gearNeeds ?? []) : (gearNeeds ?? []).slice(0, INITIAL_GEAR_VISIBLE)
  const hasMoreGear = (gearNeeds?.length ?? 0) > INITIAL_GEAR_VISIBLE
  const moreCount = (gearNeeds?.length ?? 0) - INITIAL_GEAR_VISIBLE

  const sectionTitle = (
    <>
      <span>{title}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="What is the gear checklist?"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-xs">
          <p className="text-sm text-muted-foreground leading-relaxed">{GEAR_CHECKLIST_TOOLTIP_COPY}</p>
        </TooltipContent>
      </Tooltip>
    </>
  )

  if (isLoading) {
    return (
      <SectionCard title={sectionTitle} count={0} className={className} fixedHeight>
        <div className="animate-pulse h-20 rounded-lg bg-earth-100 dark:bg-earth-800" />
      </SectionCard>
    )
  }

  const addGearButton = !isGuestAccess && (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setSelectedDetail({ type: 'gear-add' })}
      className="gap-2"
    >
      <Plus className="h-4 w-4" />
      Add gear
    </Button>
  )

  return (
    <SectionCard
      title={sectionTitle}
      count={gearNeeds?.length ?? 0}
      className={className}
      actions={addGearButton}
      contentClassName="pt-2"
      fixedHeight
    >
      {!gearNeeds?.length ? (
        <div className="space-y-2">
          <p className="text-sm text-earth-500 dark:text-earth-400 py-1.5">No gear items yet.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm text-earth-600 dark:text-earth-400 mb-2">
            <div className="flex-1 h-1.5 rounded-full bg-earth-200 dark:bg-earth-700 overflow-hidden">
              <div
                className="h-full bg-adventure-500 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="tabular-nums flex-shrink-0 text-xs">
              {claimedCount}/{totalNeeded}
            </span>
          </div>
          <ul className="space-y-0.5">
            {displayNeeds.map((need) => {
              const claims = need.claims ?? []
              const { totalConfirmedQuantity } = getGearQuantities(need)
              const isClaimed = totalConfirmedQuantity >= need.quantity_needed
              const isSelected = currentGearId === need.id
              return (
                <li key={need.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedDetail({ type: 'gear', id: need.id })}
                    className={cn(
                      'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                      isSelected
                        ? 'border border-adventure-500 bg-adventure-50 text-adventure-900 dark:border-transparent dark:bg-adventure-900/30 dark:text-adventure-200'
                        : 'border border-transparent bg-background text-foreground hover:bg-earth-100 dark:border-transparent dark:bg-transparent dark:hover:bg-earth-200'
                    )}
                  >
                    <span className="flex-shrink-0 text-earth-500 dark:text-earth-400">
                      {isClaimed ? (
                        <CheckCircle className="h-4 w-4 text-success" />
                      ) : (
                        <span className="h-4 w-4 rounded-full border-2 border-earth-300 dark:border-earth-600 inline-block" />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{need.name}</div>
                      {claims.length > 0 && (
                        <div className="text-xs text-earth-500 dark:text-earth-400 truncate">
                          {claims
                            .map((c) => c.member?.user?.name)
                            .filter((n): n is string => Boolean(n))
                            .join(', ')}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
          {hasMoreGear && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="mt-2 text-xs text-adventure-600 hover:text-adventure-700 dark:text-foreground dark:hover:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 focus-visible:ring-offset-1 rounded"
              aria-expanded={expanded}
              aria-label={expanded ? 'Show fewer gear items' : `Show ${moreCount} more gear items`}
            >
              {expanded ? 'Show less' : `Show more (${moreCount} more)`}
            </button>
          )}
        </>
      )}
    </SectionCard>
  )
}
